import { ArrowDown, ArrowUp, FileText, List as ListIcon, Plus, Trash2 } from 'lucide-react';
import { Button } from '@kss/ui';

/**
 * Mirrors the storefront `ShopSection` shape (apps/web/src/shop/data.ts)
 * — kept as a local copy here so the panel doesn't take a cross-app
 * dependency on the public-web sources.
 */
export interface PanelSectionAttribute {
  name: string;
  value: string;
}

export type PanelSection =
  | { name: string; type: 'text'; content: string; features: string[] }
  | { name: string; type: 'keyvalue'; attributes: PanelSectionAttribute[] };

export interface SectionsEditorProps {
  value: PanelSection[];
  onChange: (next: PanelSection[]) => void;
}

/**
 * Authoring UI for the dynamic accordion that ships in the storefront
 * product page. Each section has:
 *   - a name (header label)
 *   - a type: `text` (paragraph + bullet features) OR `keyvalue`
 *     (Brand: Knottique, SKU: SKU000003, …)
 *
 * On save, the parent form serialises this array into the
 * `Product.sectionsJson` column (NULL-safe: a 0-length array is
 * stored as `undefined` so the backend column stays NULL until the
 * admin actually adds content).
 *
 * Mirrors the JSON contract used by the AI product-creation
 * assistant — paste its output verbatim and it will round-trip.
 */
export default function SectionsEditor({ value, onChange }: SectionsEditorProps) {
  const update = (i: number, patch: Partial<PanelSection>) => {
    const next = value.slice();
    next[i] = { ...next[i], ...patch } as PanelSection;
    onChange(next);
  };
  const addText = () => {
    onChange([...value, { name: 'New section', type: 'text', content: '', features: [] }]);
  };
  const addKeyValue = () => {
    onChange([...value, { name: 'New section', type: 'keyvalue', attributes: [{ name: '', value: '' }] }]);
  };
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const moveUp = (i: number) => {
    if (i === 0) return;
    const next = value.slice();
    [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
    onChange(next);
  };
  const moveDown = (i: number) => {
    if (i === value.length - 1) return;
    const next = value.slice();
    [next[i + 1], next[i]] = [next[i]!, next[i + 1]!];
    onChange(next);
  };

  const inputCls =
    'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
  const textareaCls = `${inputCls} h-auto py-2`;

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2">
        <div className="text-xs text-content-secondary">
          {value.length} section{value.length === 1 ? '' : 's'} — first one is open by default on
          the storefront. <span className="text-content-muted">Tip: key-value attributes here are
          free-text labels (e.g. "Brand" / "Knottique"), not linked to the canonical Attribute
          catalog from the Attributes tab.</span>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" onClick={addText}>
            <FileText className="h-3.5 w-3.5" /> Add text
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={addKeyValue}>
            <ListIcon className="h-3.5 w-3.5" /> Add key-value
          </Button>
        </div>
      </div>

      {value.length === 0 && (
        <div className="rounded-md border border-dashed border-border bg-surface-card-hover p-6 text-center text-xs text-content-muted">
          No sections yet. The storefront will fall back to a derived
          Description / Specifications / Shipping default.
        </div>
      )}

      {value.map((sec, i) => (
        <div
          key={i}
          className="grid gap-2 rounded-md border border-border bg-surface-card-hover p-3"
        >
          <div className="flex items-start gap-2">
            <input
              value={sec.name}
              onChange={(e) => update(i, { name: e.target.value } as Partial<PanelSection>)}
              placeholder="Section name (e.g. Description)"
              maxLength={64}
              className={`${inputCls} font-semibold`}
            />
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => moveUp(i)}
                disabled={i === 0}
                title="Move up"
                className="inline-flex h-9 w-9 items-center justify-center rounded text-content-secondary hover:bg-surface-card disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => moveDown(i)}
                disabled={i === value.length - 1}
                title="Move down"
                className="inline-flex h-9 w-9 items-center justify-center rounded text-content-secondary hover:bg-surface-card disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                title="Delete section"
                className="inline-flex h-9 w-9 items-center justify-center rounded text-danger hover:bg-surface-card"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-wide text-content-muted">
            type · {sec.type}
          </div>

          {sec.type === 'text' ? (
            <TextSectionBody section={sec} onChange={(patch) => update(i, patch)} inputCls={inputCls} textareaCls={textareaCls} />
          ) : (
            <KeyValueSectionBody section={sec} onChange={(patch) => update(i, patch)} inputCls={inputCls} />
          )}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function TextSectionBody({
  section,
  onChange,
  inputCls,
  textareaCls,
}: {
  section: Extract<PanelSection, { type: 'text' }>;
  onChange: (patch: Partial<PanelSection>) => void;
  inputCls: string;
  textareaCls: string;
}) {
  const setFeature = (i: number, v: string) => {
    const next = section.features.slice();
    next[i] = v;
    onChange({ features: next } as Partial<PanelSection>);
  };
  const addFeature = () =>
    onChange({ features: [...section.features, ''] } as Partial<PanelSection>);
  const removeFeature = (i: number) =>
    onChange({ features: section.features.filter((_, idx) => idx !== i) } as Partial<PanelSection>);

  return (
    <div className="grid gap-2">
      <textarea
        value={section.content}
        onChange={(e) => onChange({ content: e.target.value } as Partial<PanelSection>)}
        rows={3}
        placeholder="Free-text content shown when the section expands."
        className={textareaCls}
      />
      <div className="grid gap-1">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-content-muted">
            Bullet features ({section.features.length})
          </span>
          <button
            type="button"
            onClick={addFeature}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
          >
            <Plus className="h-3 w-3" /> Add feature
          </button>
        </div>
        {section.features.map((f, fi) => (
          <div key={fi} className="flex items-center gap-2">
            <span className="text-content-muted">•</span>
            <input
              value={f}
              onChange={(e) => setFeature(fi, e.target.value)}
              placeholder="Bullet text"
              maxLength={120}
              className={inputCls}
            />
            <button
              type="button"
              onClick={() => removeFeature(fi)}
              aria-label="Remove feature"
              className="inline-flex h-9 w-9 items-center justify-center rounded text-content-muted hover:bg-surface-card hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function KeyValueSectionBody({
  section,
  onChange,
  inputCls,
}: {
  section: Extract<PanelSection, { type: 'keyvalue' }>;
  onChange: (patch: Partial<PanelSection>) => void;
  inputCls: string;
}) {
  const setAttr = (i: number, patch: Partial<PanelSectionAttribute>) => {
    const next = section.attributes.slice();
    next[i] = { ...next[i]!, ...patch };
    onChange({ attributes: next } as Partial<PanelSection>);
  };
  const addAttr = () =>
    onChange({
      attributes: [...section.attributes, { name: '', value: '' }],
    } as Partial<PanelSection>);
  const removeAttr = (i: number) =>
    onChange({
      attributes: section.attributes.filter((_, idx) => idx !== i),
    } as Partial<PanelSection>);

  return (
    <div className="grid gap-1">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-content-muted">
          Attributes ({section.attributes.length})
        </span>
        <button
          type="button"
          onClick={addAttr}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
        >
          <Plus className="h-3 w-3" /> Add attribute
        </button>
      </div>
      {section.attributes.map((a, i) => (
        <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2">
          <input
            value={a.name}
            onChange={(e) => setAttr(i, { name: e.target.value })}
            placeholder="Name (e.g. Brand)"
            maxLength={64}
            className={inputCls}
          />
          <input
            value={a.value}
            onChange={(e) => setAttr(i, { value: e.target.value })}
            placeholder="Value (e.g. Knottique)"
            maxLength={256}
            className={inputCls}
          />
          <button
            type="button"
            onClick={() => removeAttr(i)}
            aria-label="Remove attribute"
            className="inline-flex h-9 w-9 items-center justify-center rounded text-content-muted hover:bg-surface-card hover:text-danger"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
